import {
  ColDef,
  GridReadyEvent,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { useTranslation } from "react-i18next";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { GraphQLClient } from "graphql-request";
import AgGrid from "../../../UI/AgGrid";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";
import { CURRENT_STOCK_BY_WAREHOUSE_REPORT_VIEW } from "../../../../services/AgGrid/Report/InventoryReportAggrid";

interface Props {
  gridRef: RefObject<AgGridReact>;
}

const CurrentStockByWarehouseReportTable = ({ gridRef }: Props) => {
  const { t } = useTranslation();
  const columnDefs: ColDef[] = [
    {
      field: "unique_id",
      headerName: t("inventory.items.unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "sku_name",
      headerName: t("inventory.items.sku"),
      filter: "agTextColumnFilter",
    },
    {
      field: "name",
      headerName: t("inventory.items.name"),
      filter: "agTextColumnFilter",
    },
    {
      field: "branch_id",
      headerName: t("branch.unique_id"),
      filter: "agNumberColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "branch_unique_id",
      headerName: t("branch.unique_id"),
      filter: "agNumberColumnFilter",
    },
    {
      field: "branch_name",
      headerName: t("branch.index"),
      filter: "agTextColumnFilter",
    },
    {
      field: "warehouse_id",
      headerName: t("inventory.warehouseId"),
      filter: "agNumberColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "warehouse_unique_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "warehouse_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
    },
    {
      field: "available_qty",
      headerName: t("inventory.quantities.available_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "purchase_ordered_qty",
      headerName: t("inventory.quantities.purchase_ordered_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "transit_qty",
      headerName: t("inventory.quantities.in_transit_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "sale_committed_qty",
      headerName: t("inventory.quantities.sale_committed_qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "min_purchase_request_qty",
      headerName: "จำนวนสั่งซื้อขั้นต่ำ",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "min_stock_qty",
      headerName: "Min Stock",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        return params.value
          ? params.value.toLocaleString()
          : params.value === 0
          ? 0
          : "-";
      },
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "max_stock_qty",
      headerName: "Max Stock",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        return params.value
          ? params.value.toLocaleString()
          : params.value === 0
          ? 0
          : "-";
      },
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "reorder_point",
      headerName: t("inventory.items.reorder_point"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "status",
      headerName: t("inventory.status"),
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["ต้องสั่งซื้อ", "เกินสต็อก", "ปกติ"],
      },
      cellStyle: (params) => {
        if (params.value === "ปกติ") {
          //mark police cells as red
          return { color: "#246527", fontWeight: 600 };
        } else if (params.value === "เกินสต็อก") {
          return { color: "#C3762E", fontWeight: 600 };
        } else {
          return { color: "#B54839", fontWeight: 600 };
        }
      },
    },
  ];

  const graphQLClient: GraphQLClient = createGraphQLClientWithMiddleware("wms");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      try {
        const { CurrentStockByWarehouseReportView } =
          await graphQLClient.request(CURRENT_STOCK_BY_WAREHOUSE_REPORT_VIEW, {
            aggridInput: {
              startRow,
              endRow,
              filterModel,
              sortModel,
            },
          });
        params.success({
          rowData: CurrentStockByWarehouseReportView.results,
          rowCount: CurrentStockByWarehouseReportView.count,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onGridReady = (params: GridReadyEvent) => {
    params.api.setServerSideDatasource(datasource);
  };

  return (
    <AgGrid
      height={665}
      ref={gridRef}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
    />
  );
};

export default CurrentStockByWarehouseReportTable;
